import _objectSpread from "@babel/runtime/helpers/objectSpread";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useState, useRef } from 'react';
import useRefCallback from '@hzdg/use-ref-callback';
import { getScrollPosition } from './useScrollPosition';
import { useNearestScrollNodeRef, useScrollEffect } from './utils';
var INITIAL_SCROLL_DIRECTION = {
  vertical: null,
  horizontal: null
};
/**
 * `ScrollDirection` is an enum of possible scroll direction states.
 */

export var ScrollDirection;
(function (ScrollDirection) {
  ScrollDirection["DOWN"] = "down";
  ScrollDirection["UP"] = "up";
  ScrollDirection["LEFT"] = "left";
  ScrollDirection["RIGHT"] = "right";
})(ScrollDirection || (ScrollDirection = {}));
export var DOWN = ScrollDirection.DOWN;
export var UP = ScrollDirection.UP;
export var LEFT = ScrollDirection.LEFT;
export var RIGHT = ScrollDirection.RIGHT;

/**
 * `getScrollDirection` compares two `ScrollPosition` objects
 * and returns a `ScrollDirectionState` object.
 *
 * `ScrollDirectionState.vertical` will be one of `ScrollDirection.UP`
 * or `ScrollDirection.DOWN`, while `ScrollDirectionState.horizontal`
 * will be one of `ScrollDirection.LEFT` or `ScrollDirection.RIGHT`.
 */
export function getScrollDirection(position, lastPosition) {
  var vertical = null;
  var horizontal = null;
  if (typeof position.top === 'number') {
    vertical = position.top > 0 ? DOWN : null;
    if (lastPosition && typeof lastPosition.top === 'number') {
      if (lastPosition.top === position.top) vertical = null;else if (lastPosition.top > position.top) vertical = UP;
    }
  }
  if (typeof position.left === 'number') {
    horizontal = position.left > 0 ? RIGHT : null;
    if (lastPosition && typeof lastPosition.left === 'number') {
      if (lastPosition.left === position.left) horizontal = null;else if (lastPosition.left > position.left) horizontal = LEFT;
    }
  }
  return {
    vertical: vertical,
    horizontal: horizontal
  };
}
/**
 * `useScrollDirection` is a React hook for components that care about
 * the nearest scrollable container's scroll direction.
 *
 * If a `providedRef` is passed to `useScrollDirection`,
 * returns a `ScrollDirection` value.
 *
 * If no `providedRef` is passed, returns an array containing a
 * `ScrollDirection` object and a callback `ref`. The `ref` should be passed
 * to an underlying DOM node. Note that the node does not have to be scrollable itself,
 * as `useScrollDirection` will traverse the DOM to find a scrollable parent
 * to observe.
 *
 * `ScrollDirectionState.vertical` will be one of `ScrollDirection.UP`
 *  or `ScrollDirection.DOWN`, while `ScrollDirectionState.horizontal`
 * will be one of `ScrollDirection.LEFT` or `ScrollDirection.RIGHT`.
 */

function useScrollDirection(providedRef) {
  var scrollPosition = useRef(null);
  var _useState = useState(INITIAL_SCROLL_DIRECTION),
    _useState2 = _slicedToArray(_useState, 2),
    direction = _useState2[0],
    setDirection = _useState2[1]; // Keep a ref to the nearest scrollable container.

  var _useRefCallback = useRefCallback(null),
    _useRefCallback2 = _slicedToArray(_useRefCallback, 2),
    ref = _useRefCallback2[0],
    setRef = _useRefCallback2[1];
  if (providedRef) setRef(providedRef.current);
  var scrollRef = useNearestScrollNodeRef(ref); // Subscribe to scroll events on the nearest scrolling element,
  // calling the handler whenever a scroll event occurs.

  useScrollEffect(scrollRef, function handleScrollChange(event) {
    var nextPosition = getScrollPosition(event);
    var next = getScrollDirection(nextPosition, scrollPosition.current);
    scrollPosition.current = nextPosition;
    setDirection(function (prev) {
      var state = prev;
      if (next.horizontal !== null && next.horizontal !== state.horizontal) {
        state = _objectSpread({}, state, {
          horizontal: next.horizontal
        });
      }
      if (next.vertical !== null && next.vertical !== state.vertical) {
        state = _objectSpread({}, state, {
          vertical: next.vertical
        });
      }
      return state;
    });
  }, []); // If a ref has been provided, just return the `direction` value.
  // If a ref has not not been provided, return a callback ref
  // along with the `direction` value.

  return providedRef ? direction : [direction, setRef];
}
export default useScrollDirection;